import { useMutation, useQueryClient } from '@tanstack/react-query';
import { put } from '@truefit/http-utils';

import { ConfigDetail, configRegistryKeys } from './useConfigsQuery';

export interface UpdateConfigVariables {
  key: string;
  value: string;
  /**
   * Three-valued on purpose, and the server reads it the same way:
   *  - omitted   -> keep the existing schema
   *  - null      -> clear the schema
   *  - an object -> replace it
   *
   * So this is `undefined`, not `null`, when the editor did not touch the
   * schema. Sending null would silently drop a key's validation contract.
   */
  schema?: unknown | null;
  note: string | null;
  /**
   * The version the operator loaded. The server compares-and-swaps on it and
   * returns 409 VERSION_CONFLICT if the row moved, so a stale tab cannot
   * overwrite someone else's save.
   */
  expectedVersion: number;
}

export const useUpdateConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ key, ...body }: UpdateConfigVariables): Promise<ConfigDetail> => {
      const { data } = await put<ConfigDetail>(
        `/v1.0/internals/configs/${encodeURIComponent(key)}`,
        body,
      );
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: configRegistryKeys.all }),
  });
};

export default useUpdateConfigMutation;
